import { rem } from 'polished'
import type { ChangeEvent, FormEvent } from 'react'
import { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import TextInput from 'components/Common/Form/Input/TextInput'
import { mediaQueryDown } from 'components/utils/breakpoint'

const StaffAccessFormContainer = styled.form`
  display: grid;
  grid-template-columns: minmax(${rem(180)}, 1fr) auto;
  gap: ${rem(12)};
  align-items: end;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding-top: ${rem(16)};

  ${mediaQueryDown.tablet} {
    grid-template-columns: 1fr;
  }
`

const HeadingWrapper = styled.div`
  grid-column: 1 / -1;
`

interface Props {
  onOpenChat: (chatId: number) => void
}

function StaffChatAccessForm({ onOpenChat }: Props) {
  const intl = useIntl()
  const [chatId, setChatId] = useState('')
  const [hasError, setHasError] = useState(false)

  const handleChatIdChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setChatId(event.currentTarget.value)
    if (hasError) {
      setHasError(false)
    }
  }, [hasError])

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedChatId = chatId.trim()
    if (!/^[1-9]\d*$/.test(trimmedChatId)) {
      setHasError(true)
      return
    }

    onOpenChat(Number(trimmedChatId))
  }, [chatId, onOpenChat])

  return (
    <StaffAccessFormContainer noValidate onSubmit={handleSubmit}>
      <HeadingWrapper>
        <BodyText variant="medium" weight="bold" align="start">
          <FormattedMessage
            id="3yJaEqkPZqhwiUzywZhH"
            defaultMessage="Staff tools"
            description="Heading for staff-only tools on the TripPlanner chat landing page"
          />
        </BodyText>
      </HeadingWrapper>
      <TextInput
        label={intl.formatMessage({
          id: 'e-thg3nqpxn6bw6iip-7',
          defaultMessage: 'Spoof conversation ID',
          description: 'Input label for spoofing a TripPlanner conversation by ID',
        })}
        placeholder={intl.formatMessage({
          id: 'k0dPTYOEDJhekrYiYjlN',
          defaultMessage: 'e.g. 4157',
          description: 'Placeholder for the staff TripPlanner conversation ID input',
        })}
        value={chatId}
        onChange={handleChatIdChange}
        required
        inputMode="numeric"
        pattern="[1-9][0-9]*"
        invalidErrorMessage={intl.formatMessage({
          id: 'CYA1Zm45uBKcjs5xnwG1',
          defaultMessage: 'Use a numeric conversation ID.',
          description: 'Validation error shown when a staff user enters an invalid TripPlanner conversation ID',
        })}
        manualError={hasError}
        noValidationSpacing={!hasError}
      />
      <TextButton kind="secondary" size="large" type="submit">
        <FormattedMessage
          id="sLSZ-S8ifR1IOsC5qiio"
          defaultMessage="Spoof conversation"
          description="Button label for spoofing a TripPlanner conversation by ID"
        />
      </TextButton>
    </StaffAccessFormContainer>
  )
}

export default StaffChatAccessForm
